import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {ModalComponent} from '@appkit4/angular-components/modal';
import {DSTStatement} from '../../shared/models/dst-statement.model';
import {CopyReplicateService} from './copy-replicate.service';
import {DSTTaxYear, DSTTaxYearPeriod} from '../create-disclosure/create-new/create-disclosure.service';
import {Alert, StatementTrackerService} from '../statement-tracker.service';
import {CreateDisclosureSharedService} from '../create-disclosure/shared/create-disclosure-shared.service';
import {TrackerTabsService, TrackerTabState} from '../tracker-tabs.service';
import { StmtData } from '../../shared/models/dst-form-state.model';
import {DSTSharedService} from '../../shared/dst-shared.service';

@Component({
    selector: 'dst-copy-replicate',
    templateUrl: './copy-replicate.component.html',
    styleUrls: ['./copy-replicate.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CopyReplicateComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);


  @Input() rowObject!: DSTStatement;
  @Input() mode!: string;
  @Output() afterModalClosed: EventEmitter<null>;

  @ViewChild('baseModal', {static: false}) baseModal!: ModalComponent;

  taxYears: any[] = [];
  selectedTaxYear!: DSTTaxYear;
  taxYearPeriods: any[] = [];
  selectedPeriod!: DSTTaxYearPeriod;
  selectorDefaultText: string = 'Loading...';
  showDisclosure: boolean = false;
  copiedStatementId!: string;
  filtersLoaded: boolean = false;
  loadingStatementId: boolean = false;
  loadingStatementData: boolean = false;
  alert!: Alert;

  constructor(private cRService: CopyReplicateService,
              private trackerTabsService: TrackerTabsService,
              private dstSharedService: DSTSharedService,
              private sharedService: CreateDisclosureSharedService,
              private changeDetectorRef: ChangeDetectorRef) {
    this.afterModalClosed = new EventEmitter<null>();
    this.alert = this.dstSharedService.alert;
  }

  ngOnInit(): void {
    this.cRService.getFilterValues().pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: any[]) => {
        this.taxYears = (response[0] || []).map((el: any) => ({ ...el, value: el.id, label: String(el.text) }));
        this.taxYearPeriods = (response[1] || []).map((el: any) => ({ ...el, value: el.id, label: el.text }));
        this.selectDefaults();
        this.filtersLoaded = true;
      }, (eMsg: string) => {
        this.dstSharedService.showAlert('error', eMsg);
      })
      .add(() => {
        this.selectorDefaultText = 'Select here';
      });
    if (this.rowObject) {
      this.copiedStatementId = this.rowObject.statementId;
    }
  }

  private selectDefaults(): void {
    const taxYearFromRow: number = this.rowObject?.TAXYEAR;
    const index: number = this.taxYears.findIndex((year: DSTTaxYear) => year.text === taxYearFromRow);
    this.selectedTaxYear = this.taxYears[index >= 0 ? index : 0];
    this.selectedTaxYear.selected = true;
    this.selectedPeriod = this.taxYearPeriods[0];
    this.selectedPeriod.selected = true;
    // trigger change detection in the Appkit Selector Component
    this.changeDetectorRef.detectChanges();
  }

  onTaxYearChanged(event: any): void {
    const selected = Array.isArray(event?.selected) ? event.selected : (event?.selected ? [event.selected] : []);
    const change = selected[0] ?? null;
    this.selectedTaxYear = change;
  }

  onPeriodChanged(event: any): void {
    const selected = Array.isArray(event?.selected) ? event.selected : (event?.selected ? [event.selected] : []);
    const change = selected[0] ?? null;
    this.selectedPeriod = change;
  }

  continue(): void {
    if (this.mode === 'Copy') {
      this.createModalTab(this.rowObject.statementId);
      this.loadStatementData();
    } else {
      this.loadingStatementId = true;
      this.cRService.getNewStatementId(this.rowObject.statementId, this.selectedTaxYear.id, this.selectedPeriod.id).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((newStatementId: string) => {
          this.createModalTab(newStatementId);
          this.showDisclosure = true;
        }, (eMsg: string) => {
          this.dstSharedService.showAlert('error', eMsg);
        })
        .add(() => {
          this.loadingStatementId = false;
        });
    }
  }

  private loadStatementData(): void {
    // this.sharedService.setSavedStatementId(this.rowObject.statementId);
    this.loadingStatementData = true;
    this.sharedService.getLoadStatementData().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: StmtData) => {
      const statementData = data;
      statementData.isCopiedStatement = true;
      this.sharedService.getActiveFormState().formData = statementData;
      this.showDisclosure = true;
    }, (eMsg: string) => {
      console.error(eMsg);
      this.dstSharedService.showAlert('error', eMsg);
    }).add(() => {
      this.loadingStatementData = false;
    });
  }

  private createModalTab(statementId: string): void {
    const modalTab: TrackerTabState = new TrackerTabState(StatementTrackerService.ID_MODAL);
    modalTab.addFormState(this.selectedTaxYear.text,
      this.selectedTaxYear.id,
      this.selectedPeriod.id,
      statementId,
      this.rowObject.formId,
      this.rowObject.formName,
      this.rowObject.ATTFORMTYPEDESC!,
      this.rowObject.FORMTYPEID!,
      this.rowObject.cseId,
      false,
      this.mode === 'Replicate');
    this.trackerTabsService.createTrackerTab(modalTab);
  }

  showMore(id: string): void {
    this.baseModal.showModal(id);
  }

  modalClose(): void {
    this.notifyAfterModalClosed();
  }

  modalDetermine(): void {
    this.notifyAfterModalClosed();
  }

  // closeModal(id: string): void {
  //   this.baseModal.closeModal(id);
  //   this.notifyAfterModalClosed();
  // }

  closeModalOk(id: string): void {
    this.baseModal.closeModalOk(id);
    this.notifyAfterModalClosed();
  }

  private notifyAfterModalClosed(): void {
    // this.sharedService.setFormData({});
    this.afterModalClosed.emit();
  }

  setAriaLabel(item: any, contextLabel: string = 'Option'): string {
    const label = item?.label || item?.text || '-';
    return `${contextLabel} option ${label}`.trim();
  }

  onDropdownSelectItem(event: any): void {
  }

}
